import Swiper from 'swiper';
import { A11y, EffectCreative, Pagination } from 'swiper/modules';

export default () => ({
    swiper: null,
    linePathLength: 0,
    linePathProgress: 0,
    linePathAnimationFrame: null,
    linePathProgressInitialized: false,
    isDraggingLine: false,
    init() {
        this.$nextTick(() => this.setup());
    },
    setup(){
        if (!this.$refs.swiper) {
            return;
        }

        const creativeEffect = this.getCreativeEffect();

        this.swiper = new Swiper(this.$refs.swiper, {
            modules: [A11y, EffectCreative, Pagination],
            //loop: true,
            grabCursor: true,
            centeredSlides: true,
            centeredSlidesBounds: false,
            slidesPerView: 'auto',
            spaceBetween: 0,
            watchSlidesProgress: true,
            a11y: {
                nextSlideMessage: 'Další příspěvek',
                prevSlideMessage: 'Předchozí příspěvek',
            },
            pagination: {
                el: this.$refs.pagination,
                clickable: true,
                bulletClass: 'newsfeed__pagination__bullet',
                bulletActiveClass: 'newsfeed__pagination__bullet--active',
                renderBullet: (index, className) => (
                    `<button class="${className}" type="button" aria-label="Přejít na příspěvek ${index + 1}"></button>`
                ),
            },
            allowTouchMove: true,
            simulateTouch: true,
            effect: 'creative',
            creativeEffect: {
                perspective: true,
                limitProgress: 4,
                prev: {
                    translate: [`-${creativeEffect.translateX}px`, `${creativeEffect.translateY}px`, -120],
                    rotate: [0, 0, -creativeEffect.rotate],
                    origin: `50% ${creativeEffect.originY}`,
                },
                next: {
                    translate: [`${creativeEffect.translateX}px`, `${creativeEffect.translateY}px`, -120],
                    rotate: [0, 0, creativeEffect.rotate],
                    origin: `50% ${creativeEffect.originY}`,
                },
            },
            on: {
                init: swiper => this.setLinePathProgress(swiper.progress, false),
                touchStart: () => {
                    this.isDraggingLine = true;
                    this.cancelLinePathAnimation();
                },
                touchEnd: () => {
                    this.isDraggingLine = false;
                },
                progress: swiper => this.setLinePathProgress(swiper.progress, !this.isDraggingLine),
                slideChange: swiper => this.setLinePathProgress(swiper.progress, true),
            },
        });

        this.setupLinePath();
    },
    clampProgress(progress) {
        return Math.min(Math.max(progress, 0), 1);
    },
    setupLinePath() {
        const path = this.$refs.linePath;

        if (!path || typeof path.getTotalLength !== 'function') {
            return;
        }

        this.linePathLength = path.getTotalLength();
        path.style.strokeDasharray = `${this.linePathLength} ${this.linePathLength}`;
        this.setLinePathProgress(this.swiper?.progress || 0, false);
        path.style.opacity = '1';
    },
    setLinePathProgress(progress, animate = true) {
        const path = this.$refs.linePath;

        if (!path || !this.linePathLength) {
            return;
        }

        const clampedProgress = this.clampProgress(progress);

        if (!animate || !this.linePathProgressInitialized) {
            this.cancelLinePathAnimation();
            this.applyLinePathProgress(clampedProgress);
            this.linePathProgressInitialized = true;
            return;
        }

        this.animateLinePathProgress(clampedProgress);
    },
    applyLinePathProgress(progress) {
        const path = this.$refs.linePath;

        if (!path || !this.linePathLength) {
            return;
        }

        const clampedProgress = this.clampProgress(progress);

        path.style.strokeDashoffset = `${this.linePathLength * (1 - clampedProgress)}`;
        this.linePathProgress = clampedProgress;
    },
    animateLinePathProgress(targetProgress) {
        const startProgress = this.linePathProgress;
        const endProgress = this.clampProgress(targetProgress);

        if (Math.abs(startProgress - endProgress) < 0.001) {
            this.applyLinePathProgress(endProgress);
            return;
        }

        this.cancelLinePathAnimation();

        const duration = 420;
        const startTime = performance.now();

        const tick = now => {
            const elapsed = Math.min((now - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - elapsed, 3);
            const progress = startProgress + (endProgress - startProgress) * eased;

            this.applyLinePathProgress(progress);

            if (elapsed < 1) {
                this.linePathAnimationFrame = requestAnimationFrame(tick);
                return;
            }

            this.linePathAnimationFrame = null;
        };

        this.linePathAnimationFrame = requestAnimationFrame(tick);
    },
    cancelLinePathAnimation() {
        if (!this.linePathAnimationFrame) {
            return;
        }

        cancelAnimationFrame(this.linePathAnimationFrame);
        this.linePathAnimationFrame = null;
    },
    getCreativeEffect() {
        const card = this.$refs.swiper.querySelector('.newsfeed__carousel__card');
        const cardWidth = card?.offsetWidth || 420;
        const cardHeight = card?.offsetHeight || 560;
        const gap = this.getSlideGap();
        const radius = cardHeight * 4.8;
        const angle = Math.asin(Math.min((cardWidth + gap) / radius, 0.3));

        return {
            translateX: 0,
            translateY: 0,
            rotate: Number((angle * 180 / Math.PI * 0.82).toFixed(2)),
            originY: `${Math.round((radius / cardHeight + 0.5) * 100)}%`,
        };
    },
    getSlideGap() {
        const card = this.$refs.swiper.querySelector('.newsfeed__carousel__card');
        const cardWidth = card?.offsetWidth || 420;

        return Math.round(Math.max(Math.min(cardWidth * 0.5, 240), 180));
    },
    destroy() {
        this.cancelLinePathAnimation();

        if (this.swiper) {
            this.swiper.destroy(true, true);
            this.swiper = null;
        }
    }
});
